import { Col, Container, Row } from 'react-bootstrap'
import { SelectOption } from '../../../../../../components/SelectDropdown/SelectDropdown'
import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../schema'
import { IntegrationServiceId, SALESFORCE_BASED_INTEGRATIONS } from '../../../../../../common/integrations'
import { useCallback, useMemo } from 'react'
import { DescriptorType, DetailedCRMAccountIntegrationMapping, Filter, FilterOperator, GroupOperator } from '../../../../../../models/Account'
import { ObjectAndFieldSelectDropdown } from './ObjectAndFieldSelectDropdown'

type MappingKey = 'session' | 'task' | 'additionalObjects'

type NullableContactLink = {
  source: { object: string | null; field: string | null; filters?: Filter[] | null }
  target: { object: string | null; field: string | null }
}

export interface ContactLinkingRowsProps {
  serviceId: IntegrationServiceId
  linkingKey: MappingKey
  linkingKeyIndex?: number
}

export const ContactLinkingRows: React.FC<ContactLinkingRowsProps> = ({ serviceId, linkingKey, linkingKeyIndex }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()

  const contactObjectRaw = watch(`mappings.contact.objectName` as const)

  const mapContactObject = useCallback(
    (objName: string | null) => {
      if (!SALESFORCE_BASED_INTEGRATIONS.includes(serviceId)) return objName
      if (objName === 'person_account') return 'Account'
      if (objName === 'account') return 'Account'
      if (objName === 'contact') return 'Contact'
      return objName
    },
    [serviceId],
  )

  const contactObject = useMemo(() => mapContactObject(contactObjectRaw ?? null), [contactObjectRaw, mapContactObject])
  const mappings = watch('mappings')
  const mappingValue = mappings?.[linkingKey as keyof typeof mappings]

  const currentMapping =
    linkingKeyIndex !== undefined && Array.isArray(mappingValue)
      ? (mappingValue[linkingKeyIndex] as DetailedCRMAccountIntegrationMapping)
      : (mappingValue as DetailedCRMAccountIntegrationMapping)

  const targetObjectName = currentMapping?.objectName || null

  const contactLinkingObjects: NullableContactLink[] = currentMapping?.contactLinks as NullableContactLink[]

  const relatedToLink = useMemo(() => {
    return (
      contactLinkingObjects?.find((link) => link.source?.object === contactObject || link.source?.object === 'AccountContactRelation') || {
        source: { object: contactObject ?? null, field: null },
        target: { object: targetObjectName, field: null },
      }
    )
  }, [contactLinkingObjects, contactObject, targetObjectName])

  const linkingObjectLink = useMemo(
    () => contactLinkingObjects?.find((link) => link.source?.object === targetObjectName) || null,
    [contactLinkingObjects, targetObjectName],
  )

  const updateContactLinks = useCallback(
    (updatedLinks: NullableContactLink[]) => {
      const updatedSpecificMapping = {
        ...currentMapping,
        contactLinks: updatedLinks,
      }

      const updatedMappingValue =
        linkingKeyIndex !== undefined && Array.isArray(mappingValue)
          ? mappingValue.map((item, index) => (index === linkingKeyIndex ? updatedSpecificMapping : item))
          : updatedSpecificMapping

      const updatedMappings = {
        ...mappings,
        [linkingKey]: updatedMappingValue,
      }

      setValue('mappings', updatedMappings, { shouldDirty: true, shouldValidate: true })
    },
    [currentMapping, linkingKey, linkingKeyIndex, mappingValue, mappings, setValue],
  )

  const onRelatedToContactLinkChanged = useCallback(
    (updatedLink: NullableContactLink) => {
      const updatedLinkWithFilters: NullableContactLink =
        updatedLink.source.object === 'AccountContactRelation' && updatedLink.source.field
          ? {
              ...updatedLink,
              source: {
                ...updatedLink.source,
                filters: [
                  {
                    groupOperator: GroupOperator.AND,
                    expressions: [
                      {
                        field: 'ContactId',
                        operator: FilterOperator.EQ,
                        source: {
                          type: DescriptorType.VARIABLE,
                          value: 'contactId',
                        },
                      },
                    ],
                  },
                ],
              },
            }
          : updatedLink

      const updatedAdditionalLinks =
        updatedLinkWithFilters.target.object !== targetObjectName
          ? linkingObjectLink
            ? { ...linkingObjectLink, target: { object: updatedLinkWithFilters.target.object, field: linkingObjectLink.target.field } }
            : {
                source: { object: targetObjectName ?? null, field: updatedLinkWithFilters.target.field },
                target: { object: updatedLinkWithFilters.target.object, field: null },
              }
          : null

      const updatedLinks =
        updatedLink.target.object !== targetObjectName
          ? [updatedLinkWithFilters, ...(updatedAdditionalLinks ? [updatedAdditionalLinks] : [])]
          : [updatedLinkWithFilters]

      updateContactLinks(updatedLinks)
    },
    [linkingObjectLink, targetObjectName, updateContactLinks],
  )

  const onLinkingObjectContactLinkChanged = useCallback(
    (updatedLink: NullableContactLink) => {
      const updatedLinks = [relatedToLink, updatedLink]
      updateContactLinks(updatedLinks)
    },
    [relatedToLink, updateContactLinks],
  )

  const predefinedRelatedToSourceObjectOptions: SelectOption[] = useMemo(() => {
    const options: SelectOption[] = []
    if (contactObject) {
      options.push({
        id: contactObject,
        label: contactObject,
      })
    }
    if (SALESFORCE_BASED_INTEGRATIONS.includes(serviceId))
      options.push({
        id: 'AccountContactRelation',
        label: 'AccountContactRelation',
      })
    return options
  }, [contactObject, serviceId])

  return (
    <Container>
      <Row key='relatedToLinkRow' className='mb-3 align-items-center'>
        <Col className='d-flex align-items-center'>
          <ObjectAndFieldSelectDropdown
            serviceId={serviceId}
            objectName={relatedToLink?.source?.object || null}
            initialSelectedField={relatedToLink?.source?.field || null}
            predefinedObjectSelectOptions={predefinedRelatedToSourceObjectOptions}
            onObjectSelected={(opt) => {
              onRelatedToContactLinkChanged({
                source: { object: opt.id as string, field: relatedToLink.source.object === opt.id ? relatedToLink.source.field : null },
                target: relatedToLink.target,
              })
            }}
            onFieldSelected={(opt) => {
              onRelatedToContactLinkChanged({ source: { object: relatedToLink.source.object, field: opt.id as string }, target: relatedToLink.target })
            }}
          />
        </Col>
        <Col className='d-flex align-items-center'>
          <i className='bi bi-arrow-right mx-3 zocks-text-gray-500 fs-5'></i>
          <ObjectAndFieldSelectDropdown
            serviceId={serviceId}
            objectName={relatedToLink?.target?.object || null}
            initialSelectedField={relatedToLink?.target?.field || null}
            onObjectSelected={(opt) => {
              onRelatedToContactLinkChanged({
                source: relatedToLink.source,
                target: { object: opt.id as string, field: relatedToLink.target.object === opt.id ? relatedToLink.target.field : null },
              })
            }}
            onFieldSelected={(opt) => {
              onRelatedToContactLinkChanged({ source: relatedToLink.source, target: { object: relatedToLink.target.object, field: opt.id as string } })
            }}
          />
        </Col>
      </Row>
      {linkingObjectLink ? (
        <Row key='linkingObjectLinkRow' className='mb-3 align-items-center'>
          <Col className='d-flex align-items-center'>
            <ObjectAndFieldSelectDropdown
              serviceId={serviceId}
              objectName={linkingObjectLink?.source?.object || null}
              initialSelectedField={linkingObjectLink?.source?.field || null}
              onObjectSelected={() => {}}
              onFieldSelected={(opt) => {
                onLinkingObjectContactLinkChanged({
                  source: { object: linkingObjectLink.source.object, field: opt.id as string },
                  target: linkingObjectLink.target,
                })
              }}
              isObjectSelectionDisabled
            />
          </Col>
          <Col className='d-flex align-items-center'>
            <i className='bi bi-arrow-right mx-3 zocks-text-gray-500 fs-5'></i>
            <ObjectAndFieldSelectDropdown
              serviceId={serviceId}
              objectName={linkingObjectLink?.target?.object || null}
              initialSelectedField={linkingObjectLink?.target?.field || null}
              onObjectSelected={() => {}}
              onFieldSelected={(opt) => {
                onLinkingObjectContactLinkChanged({
                  source: linkingObjectLink.source,
                  target: { object: linkingObjectLink.target.object, field: opt.id as string },
                })
              }}
              isObjectSelectionDisabled
            />
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}
